/**
 * AdminStatGrid — 4-tile KPI header for /admin (Directive §2 rebuild).
 *
 * Uses the shared MetricCardHeader primitive so every KPI ships with:
 *   • Inline delta badge in the accessible data palette (blue / orange / gray)
 *   • Placeholder for a live sparkline (fed by AdminPulseStrip's ring buffer
 *     when we wire trend history into /api/admin/summary).
 *   • Height-standardized card container (min-h-[180px]) — matches the
 *     dashboard cadence without over-reserving vertical space for
 *     single-number tiles.
 */
import { Coins, ShieldAlert, TrendingUp, Users } from "lucide-react";
import { fmtKES } from "@/lib/api";
import MetricCardHeader from "@/components/dashboard/MetricCardHeader";
import SkeletonGrid from "@/components/SkeletonGrid";

function StatBig({ icon: Icon, label, value, sub, delta, spark, sparkTone, testId }) {
    return (
        <div
            className="flex flex-col p-6 density-compact:p-3.5 bg-card rounded-lg border border-border hover:bg-muted/40 transition-colors min-h-[140px]"
            data-testid={testId ? `${testId}-card` : undefined}
        >
            <MetricCardHeader
                icon={Icon}
                title={label}
                value={value}
                subtitle={sub}
                delta={delta}
                spark={spark}
                sparkTone={sparkTone}
                testid={testId}
            />
        </div>
    );
}

export function AdminStatGrid({ summary }) {
    if (!summary) return <SkeletonGrid variant="cards" count={4} />;
    const perTier = summary.per_tier || {};
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 density-compact:gap-3 mb-6" data-testid="admin-stat-grid">
            <StatBig
                icon={Coins}
                label="Gross revenue"
                value={fmtKES(summary.total_gross_revenue_kes)}
                sub={`Sub: ${fmtKES(summary.total_subscription_revenue_kes)} · Comm: ${fmtKES(summary.total_marketplace_commission_kes)}`}
                testId="admin-gross-rev"
            />
            <StatBig
                icon={Users}
                label="Total users"
                value={summary.total_users}
                sub={`Individual ${perTier.individual || 0} · MoE ${perTier.moe_institution || 0} · CMA ${perTier.cma_institution || 0}`}
                testId="admin-total-users"
            />
            <StatBig
                icon={TrendingUp}
                label="Referral signups"
                value={summary.referral_signups}
                testId="admin-referral-signups"
            />
            <StatBig
                icon={ShieldAlert}
                label="Breached accounts"
                value={summary.breached_accounts}
                testId="admin-breached-accounts"
            />
        </div>
    );
}
